<template>
  <section v-if="visible" class="image-carousel-block">
    <div class="image-carousel-block__inner">
      <div class="image-carousel-block__content">
        <swiper
            slides-per-view="auto"
            :breakpoints="bps"
            class="image-carousel-block__slider swiper-fade-in"
            @slide-change="onSlideChange"
            @swiper="useSwiper"
        >
          <swiper-slide
              v-for="(imageRef, index) in data.images"
              :key="`slide_${index}`"
              class="image-carousel-block__slider-slide"
          >
            <AppImageCarouselItem
                :data="imageRef.image_block_id"
            />
          </swiper-slide>
          <div class="image-carousel-block__wrapper" :class="{'image-carousel-block__wrapper--mobile': state.isMobile}">
            <AppCarouselButton
                :visible="state.showLeft"
                :full="state.leftFullWidth"
                direction="left"
                class="image-carousel-block__carousel-button"
                aria-label="Vorige afbeelding"
            />
            <AppCarouselButton
                :visible="state.showRight"
                :full="state.rightFullWidth"
                direction="right"
                class="image-carousel-block__wrapper-carousel-button"
                aria-label="Volgende afbeelding"
            />
          </div>
        </swiper>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/navigation'
import {computed, onMounted, reactive} from 'vue'
import {TImageCarouselBlock} from '~/types/block-types'
import {breakpoints} from '~/constants/breakpoints'
import {AppCarouselButton} from '#components'
import {useBrowserSupportStore} from '~/store/browser-support'
import {useSwiper} from "~/composables/useSwiper";
import {isVisible} from "~/utils/visibility";

const browserSupportStore = useBrowserSupportStore()

const props = defineProps<{
  data: TImageCarouselBlock,
}>()

const visible = computed(() => {
  return isVisible(props.data)
})

const state = reactive({
  showLeft: false,
  showRight: true,
  leftFullWidth: false,
  rightFullWidth: true,
  isMobile: true
})

onMounted(() => {
  state.isMobile = browserSupportStore.isMobileDevice
})

const onSlideChange = (swiper) => {
  const first = swiper.activeIndex === 0
  const last = swiper.activeIndex === swiper.slides.length - 1
  if (first || last) {
    if (first) {
      state.showLeft = state.leftFullWidth = false
      state.showRight = state.rightFullWidth = true
    }
    if (last) {
      state.showLeft = state.leftFullWidth = true
      state.showRight = state.rightFullWidth = false
    }
  } else {
    state.showLeft = state.showRight = true
    state.leftFullWidth = state.rightFullWidth = false
  }
}

const bps = {}
breakpoints.forEach((bp) => {
  switch (bp.name) {
    case 'sm':
      bps[bp.min] = {
        centeredSlides: true
      }
  }
})
</script>

<style scoped lang="postcss">
.image-carousel-block {
  position: relative;
  cursor: none;
  overflow: clip;
  @mixin app-block-margins;

  &__inner {
    @mixin app-layout-container;
  }

  &__content {
    grid-column: span 12 / span 12;

    @media (--xl) {
      grid-column: span 10 / span 10;
      grid-column-start: 2;
    }
  }

  &__slider {
    position: static;
    overflow: visible;
  }

  &__slider-slide {
    position: relative;
    margin-right: 0.5rem;
    width: fit-content;

    &:last-child {
      margin-right: 0px;
    }

    @media (--xl) {
      margin-right: 0.75rem;
    }
  }

  &__wrapper {
    &--mobile {
      display: none;
    }
  }

  &__carousel-button {
    left: 0px;
  }

  &__wrapper-carousel-button {
    right: 0px;
  }
}
</style>
